import './beleg.css';
import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { canShowPdf, PdfPages } from '../components/Pdf';
import { LegalLinks } from '../components/LegalLinks';
import { useConfig } from '../lib/config';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useApi } from '../lib/useApi';
import { isBilltoro } from '../produkt';

// Seite zum Beleg für den Kunden eines Mandanten (ohne Anmeldung): Der Knopf in der E-Mail („Rechnung ansehen“) führt
// hierher (server/src/modules/rechnungstool/link.ts). Die Seite gehört dem Absender: sein Logo, seine Farbe, seine
// Angaben. Die App steht nur klein unten. Immer hell wie Papier, auch wenn das Gerät dunkel eingestellt ist.
// Kein Zählen, wer die Seite öffnet. Deutsch wie die Rechnung selbst.
// Beim offenen, gültigen Angebot nimmt der Kunde es hier verbindlich an oder lehnt es ab (Karte „Ihre Antwort“,
// server/src/modules/rechnungstool/annahme.ts); danach steht dort die Antwort und die Auftragsbestätigung.

// wie BelegStatus in link.ts (korrektur: die Rechnungskorrektur selbst; bestaetigt und geliefert: Auftragsbestätigung und
// Lieferschein)
type Status =
  | 'offen'
  | 'teilweise'
  | 'bezahlt'
  | 'storniert'
  | 'korrektur'
  | 'gueltig'
  | 'abgelaufen'
  | 'angenommen'
  | 'abgelehnt'
  | 'bestaetigt'
  | 'geliefert';

interface Beleg {
  art: 'rechnung' | 'mahnung' | 'angebot' | 'auftrag' | 'lieferschein';
  farbe: { haupt: string; text: string; auf: string; rand: string };
  logo: boolean;
  heute: string;
  titel: string;
  nummer: string;
  firma: { name: string; anschrift: string; telefon: string | null; email: string | null; angaben: string[] };
  kunde: string;
  datum: string;
  // Lieferschein ohne Preise: kein Betrag
  betrag: number | null;
  // Währung aller Beträge (fehlt bei älteren Antworten: Euro)
  waehrung?: string;
  offen: number;
  faellig: string | null;
  gueltigBis: string | null;
  // Auftragsbestätigung: Liefertermin, Lieferschein: Lieferdatum (sonst null)
  liefertermin: string | null;
  status: Status;
  ueberfaellig: boolean;
  bezahltAm: string | null;
  stornoZu: string | null;
  // Rechnungskorrektur: die Rechnung, die sie mindert; Rechnung: ihre Korrekturen (gutgeschrieben) und was der Kunde
  // danach zurückbekommt (0: nichts)
  korrekturZu: string | null;
  korrekturen: { nummer: string; betrag: number }[];
  erstattung: number;
  rechnung: string | null;
  zahlung: {
    empfaenger: string;
    iban: string;
    bic: string | null;
    bank: string | null;
    betrag: number;
    verwendungszweck: string;
    // nur in Euro (sonst null)
    girocode: string | null;
    // in anderer Währung: „Bitte in USD überweisen.“ (sonst null)
    hinweis?: string | null;
  } | null;
  skonto: { prozent: number; bis: string; betrag: number } | null;
  // nur beim Angebot (sonst null): ob der Kunde antworten kann, ob der Absender selbst angemeldet ist, die Online-Antwort
  antwort: {
    moeglich: boolean;
    eigeneFirma: boolean;
    online: null | {
      art: 'angenommen' | 'abgelehnt';
      am: { datum: string; uhrzeit: string };
      name: string | null;
      referenz: string | null;
    };
  } | null;
  // nur beim Angebot: die gültige Auftragsbestätigung dazu (sonst null)
  auftrag: { nummer: string } | null;
  dateien: { art: 'pdf' | 'xml' | 'rechnung-pdf' | 'auftrag-pdf'; name: string; url: string }[];
  // nur Billtoro: „Erstellt mit Billtoro“ (Tarife ohne „ohneWerbung“), sonst null
  billtoroHinweis?: { text: string; url: string | null } | null;
}

const WEG = 'Dieser Link gilt nicht mehr. Bitte wenden Sie sich an den Absender.';

// Betrag zum Einfügen in die Banking-App: „202,30“
const betragText = (v: number) => v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

export function BelegPage() {
  const { token = '' } = useParams();
  const beleg = useApi<Beleg>(`/api/public/beleg/${encodeURIComponent(token)}`);
  const b = beleg.data;

  // Hell erzwingen, nicht in Suchmaschinen, keine Herkunft (mit dem Token) an andere Seiten
  useEffect(() => {
    const html = document.documentElement;
    const theme = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'light');
    const metas = [meta('robots', 'noindex, nofollow'), meta('referrer', 'no-referrer')];
    return () => {
      if (theme === null) html.removeAttribute('data-theme');
      else html.setAttribute('data-theme', theme);
      metas.forEach((m) => m.remove());
    };
  }, []);

  useEffect(() => {
    document.title = b ? `${belegName(b)} · ${b.firma.name}` : 'Beleg';
    if (!b) return;
    const tc = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const alt = tc?.content;
    if (tc) tc.content = b.farbe.haupt;
    return () => {
      if (tc && alt) tc.content = alt;
    };
  }, [b]);

  if (beleg.loading) {
    return (
      <div className="bl">
        <div className="spinner" aria-label="Lädt" />
      </div>
    );
  }
  if (!b) {
    // 404 (ungültig, abgelaufen, Firma nicht mehr dabei) oder keine Verbindung
    const weg = beleg.errorStatus === 404;
    return (
      <div className="bl">
        <main className="bl-main bl-weg">
          <div className="bl-card" role="alert">
            <h1>{weg ? 'Link nicht mehr gültig' : 'Beleg nicht erreichbar'}</h1>
            <p>{weg ? WEG : (beleg.error ?? WEG)}</p>
            {!weg && (
              <button type="button" className="bl-btn" onClick={() => void beleg.reload()}>
                Noch einmal versuchen
              </button>
            )}
          </div>
          <Fuss />
        </main>
      </div>
    );
  }
  return <Ansicht token={token} b={b} setzen={(neu) => beleg.setData(() => neu)} neuLaden={beleg.reload} />;
}

// Name wie auf der PDF: „Rechnung RE-2026-0001“, bei der Mahnung „Zahlungserinnerung zur Rechnung RE-2026-0001“ (so
// auch im Kasten der E-Mail)
const belegName = (b: Beleg) => (b.rechnung ? `${b.titel} zur ${b.rechnung}` : `${b.titel} ${b.nummer}`);

function meta(name: string, content: string) {
  const m = document.createElement('meta');
  m.name = name;
  m.content = content;
  document.head.appendChild(m);
  return m;
}

function Ansicht({ token, b, setzen, neuLaden }: { token: string; b: Beleg; setzen: (b: Beleg) => void; neuLaden: () => Promise<void> }) {
  const [logoOk, setLogoOk] = useState(b.logo);
  // nach dem Annehmen: ob die Auftragsbestätigung auch per E-Mail ging
  const [abMail, setAbMail] = useState(false);
  const vars = { '--bl-haupt': b.farbe.haupt, '--bl-text': b.farbe.text, '--bl-auf': b.farbe.auf, '--bl-rand': b.farbe.rand } as CSSProperties;
  const pdf = b.dateien.find((d) => d.art === 'pdf');
  const name = belegName(b);

  return (
    <div className="bl" style={vars}>
      <header className="bl-top">
        <div className="bl-top-in">
          {logoOk ? (
            <img className="bl-logo" src={`/api/public/beleg/${encodeURIComponent(token)}/logo`} alt={b.firma.name} onError={() => setLogoOk(false)} />
          ) : (
            <span className="bl-firm">{b.firma.name}</span>
          )}
        </div>
      </header>

      <main className="bl-main">
        <section className="bl-card bl-hero" aria-labelledby="bl-h">
          {logoOk && <p className="bl-von">{b.firma.name}</p>}
          <h1 id="bl-h">{name}</h1>
          {b.stornoZu && <p className="bl-zu">hebt die Rechnung {b.stornoZu} auf</p>}
          {b.korrekturZu && <p className="bl-zu">korrigiert die Rechnung {b.korrekturZu}</p>}

          <div className="bl-sum">
            {b.betrag !== null ? (
              <div>
                <span className="bl-lbl">{betragLabel(b)}</span>
                <span className="bl-amount">{eurC(b.zahlung ? b.offen : b.betrag, b.waehrung)}</span>
                {b.status === 'teilweise' && <span className="bl-sub">von {eurC(b.betrag, b.waehrung)}</span>}
              </div>
            ) : (
              <div />
            )}
            <Chip b={b} />
          </div>
          {b.korrekturen.length > 0 && (
            <ul className="bl-korr">
              {b.korrekturen.map((k) => (
                <li key={k.nummer}>
                  Gutgeschrieben: {eurC(k.betrag, b.waehrung)} (Rechnungskorrektur {k.nummer})
                </li>
              ))}
              {b.erstattung > 0 && <li className="bl-korr-zurueck">Sie bekommen {eurC(b.erstattung, b.waehrung)} zurück.</li>}
            </ul>
          )}

          <dl className="bl-facts">
            <div>
              <dt>{b.art === 'angebot' || b.art === 'auftrag' ? 'Für' : 'An'}</dt>
              <dd>{b.kunde}</dd>
            </div>
            <div>
              <dt>Datum</dt>
              <dd>{fmtDate(b.datum)}</dd>
            </div>
            {b.faellig && (
              <div>
                <dt>{b.art === 'mahnung' ? 'Zahlbar bis' : 'Fällig am'}</dt>
                <dd>{fmtDate(b.faellig)}</dd>
              </div>
            )}
            {b.gueltigBis && (
              <div>
                <dt>Gültig bis</dt>
                <dd>{fmtDate(b.gueltigBis)}</dd>
              </div>
            )}
            {b.liefertermin && (
              <div>
                <dt>{b.art === 'lieferschein' ? 'Lieferdatum' : 'Liefertermin'}</dt>
                <dd>{fmtDate(b.liefertermin)}</dd>
              </div>
            )}
          </dl>

          <div className="bl-act">
            {b.dateien.map((d) => (
              <a
                key={d.art}
                className={`bl-btn${d.art === 'pdf' ? ' primary' : ''}`}
                href={d.art === 'xml' ? d.url : `${d.url}?download=1`}
                download={d.name}
              >
                {dateiText(d.art)}
              </a>
            ))}
          </div>
        </section>

        {b.antwort && (
          <Antwort
            token={token}
            b={b}
            a={b.antwort}
            abMail={abMail}
            fertig={(neu, mail) => {
              setAbMail(mail);
              setzen(neu);
            }}
            neuLaden={neuLaden}
          />
        )}

        <div className="bl-grid">
          <div className="bl-side">
            {b.zahlung && <Zahlung z={b.zahlung} skonto={b.skonto} mahnung={b.art === 'mahnung'} w={b.waehrung} />}
            <Kontakt b={b} />
          </div>
          {pdf && (
            <section className="bl-card bl-doc" aria-label={`Vorschau ${name}`}>
              <div className={`bl-doc-b${canShowPdf() ? ' obj' : ''}`}>
                {canShowPdf() ? (
                  <object data={pdf.url} type="application/pdf" aria-label={name}>
                    <Keine url={pdf.url} />
                  </object>
                ) : (
                  <PdfPages url={pdf.url} title={name} fallback={<Keine url={pdf.url} />} />
                )}
              </div>
            </section>
          )}
        </div>

        <Fuss b={b} />
      </main>
    </div>
  );
}

function betragLabel(b: Beleg) {
  if (b.zahlung) return 'Zu zahlen';
  if (b.art === 'angebot') return 'Angebotssumme';
  if (b.art === 'auftrag') return 'Auftragssumme';
  if (b.art === 'lieferschein') return 'Gesamtbetrag';
  // Mahnung zu einer inzwischen bezahlten oder stornierten Rechnung: der Betrag, um den es ging, nicht „offen“ neben
  // „Bezahlt“
  if (b.art === 'mahnung') return b.titel === 'Zahlungserinnerung' ? 'Betrag der Zahlungserinnerung' : 'Betrag der Mahnung';
  return (b.betrag ?? 0) < 0 ? 'Betrag' : 'Rechnungsbetrag';
}

// Hauptknopf: der Beleg selbst; daneben die E-Rechnung als XML, bei der Mahnung die Rechnung, beim Angebot die
// Auftragsbestätigung
function dateiText(art: Beleg['dateien'][number]['art']) {
  if (art === 'xml') return 'E-Rechnung (XML)';
  if (art === 'rechnung-pdf') return 'Rechnung (PDF)';
  if (art === 'auftrag-pdf') return 'Auftragsbestätigung (PDF)';
  return 'PDF herunterladen';
}

// Stand in einem Wort: offen, bezahlt, gültig … (die Fristen stehen darunter)
function Chip({ b }: { b: Beleg }) {
  const [art, text] = chip(b);
  return <span className={`bl-chip ${art}`}>{text}</span>;
}

function chip(b: Beleg): ['info' | 'warn' | 'ok' | 'grau', string] {
  switch (b.status) {
    case 'offen':
      return b.ueberfaellig ? ['warn', 'Überfällig'] : ['info', 'Offen'];
    case 'teilweise':
      return [b.ueberfaellig ? 'warn' : 'info', 'Teilweise bezahlt'];
    case 'bezahlt':
      return ['ok', b.bezahltAm ? `Bezahlt am ${fmtDate(b.bezahltAm)}` : 'Bezahlt'];
    case 'storniert':
      return ['grau', b.stornoZu ? 'Stornorechnung' : 'Storniert'];
    case 'korrektur':
      return ['grau', 'Rechnungskorrektur'];
    case 'gueltig':
      return ['info', 'Gültig'];
    case 'abgelaufen':
      return ['grau', 'Abgelaufen'];
    case 'angenommen':
      return ['ok', 'Angenommen'];
    case 'abgelehnt':
      return ['grau', 'Abgelehnt'];
    case 'bestaetigt':
      return ['ok', 'Bestätigt'];
    case 'geliefert':
      return ['ok', 'Geliefert'];
  }
}

// Antwort des Kunden auf das Angebot (annahme.ts): annehmen oder ablehnen, solange es offen und gültig ist; danach die
// Antwort und die Auftragsbestätigung. Abgelaufen, von Hand beantwortet oder abgerechnet: keine Karte (der Chip oben
// sagt den Stand), außer eine Meldung muss stehen bleiben (z. B. die Firma hat es eben von Hand angenommen).
type Antwortstand = NonNullable<Beleg['antwort']>;
type Gesendet = Beleg & { abMail?: boolean };

function Antwort({
  token,
  b,
  a,
  abMail,
  fertig,
  neuLaden,
}: {
  token: string;
  b: Beleg;
  a: Antwortstand;
  abMail: boolean;
  fertig: (b: Beleg, abMail: boolean) => void;
  neuLaden: () => Promise<void>;
}) {
  const [form, setForm] = useState<'annehmen' | 'ablehnen' | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const kopf = useRef<HTMLHeadingElement>(null);
  const o = a.online;

  // nach dem Senden die neue Überschrift ansagen und in den Blick holen
  const vorher = useRef(o?.art ?? null);
  useEffect(() => {
    if (o && vorher.current !== o.art) kopf.current?.focus();
    vorher.current = o?.art ?? null;
  }, [o]);

  const oeffnen = (f: 'annehmen' | 'ablehnen' | null) => {
    setMeldung(null);
    setForm(f);
  };

  const senden = async (weg: 'annehmen' | 'ablehnen', body: Record<string, unknown>) => {
    setMeldung(null);
    try {
      const { abMail: mail, ...neu } = await api.post<Gesendet>(`/api/public/beleg/${encodeURIComponent(token)}/${weg}`, body);
      setForm(null);
      fertig(neu, mail === true);
      return true;
    } catch (err) {
      setMeldung(errorText(err));
      // schon beantwortet, abgelaufen, nicht mehr offen: den neuen Stand zeigen, die Meldung bleibt stehen
      if (err instanceof ApiError && err.status === 409) {
        setForm(null);
        await neuLaden();
      }
      return false;
    }
  };

  let inhalt: ReactNode;
  let titel = 'Ihre Antwort';
  if (o?.art === 'angenommen') {
    titel = 'Vielen Dank für Ihren Auftrag';
    const ab = b.dateien.find((d) => d.art === 'auftrag-pdf');
    inhalt = (
      <>
        <p>
          Sie haben das Angebot am {fmtDate(o.am.datum)} um {o.am.uhrzeit} Uhr angenommen{o.name ? ` (${o.name})` : ''}.
        </p>
        {o.referenz && <p>Ihre Bestellnummer: {o.referenz}</p>}
        {b.auftrag && ab ? (
          <>
            <p>
              Ihre Auftragsbestätigung {b.auftrag.nummer} liegt bereit.{abMail ? ' Sie kommt auch per E-Mail.' : ''}
            </p>
            <div className="bl-antwort-knoepfe">
              <a className="bl-btn primary" href={`${ab.url}?download=1`} download={ab.name}>
                {dateiText(ab.art)}
              </a>
            </div>
          </>
        ) : (
          <p>{b.firma.name} meldet sich bei Ihnen.</p>
        )}
      </>
    );
  } else if (o?.art === 'abgelehnt') {
    titel = 'Angebot abgelehnt';
    inhalt = <p>Sie haben das Angebot am {fmtDate(o.am.datum)} abgelehnt. Danke für Ihre Rückmeldung.</p>;
  } else if (a.moeglich) {
    inhalt =
      form === 'annehmen' ? (
        <AnnahmeForm b={b} senden={(body) => senden('annehmen', body)} abbrechen={() => oeffnen(null)} />
      ) : form === 'ablehnen' ? (
        <AblehnungForm firma={b.firma.name} senden={(body) => senden('ablehnen', body)} abbrechen={() => oeffnen(null)} />
      ) : (
        <>
          <p>Passt das Angebot? Sie können es hier annehmen oder ablehnen.</p>
          <div className="bl-antwort-knoepfe">
            <button type="button" className="bl-btn primary" onClick={() => oeffnen('annehmen')}>
              Angebot annehmen
            </button>
            <button type="button" className="bl-btn" onClick={() => oeffnen('ablehnen')}>
              Ablehnen
            </button>
          </div>
        </>
      );
  } else if (a.eigeneFirma && b.status === 'gueltig') {
    inhalt = <p className="bl-note">Sie sind als Absender angemeldet. Hier kann Ihr Kunde das Angebot annehmen oder ablehnen.</p>;
  } else if (!meldung) {
    return null;
  }

  return (
    <section className="bl-card bl-antwort" aria-labelledby="bl-antwort-h">
      <h2 id="bl-antwort-h" ref={kopf} tabIndex={-1}>
        {titel}
      </h2>
      {inhalt}
      {meldung && (
        <div className="notice bad" role="alert">
          {meldung}
        </div>
      )}
    </section>
  );
}

// Annehmen: Name und Haken Pflicht, Bestellnummer und Nachricht freiwillig
function AnnahmeForm({ b, senden, abbrechen }: { b: Beleg; senden: (body: Record<string, unknown>) => Promise<boolean>; abbrechen: () => void }) {
  const [name, setName] = useState('');
  const [referenz, setReferenz] = useState('');
  const [nachricht, setNachricht] = useState('');
  const [verbindlich, setVerbindlich] = useState(false);
  const [busy, setBusy] = useState(false);
  const bereit = name.trim() !== '' && verbindlich && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!bereit) return;
    setBusy(true);
    const ok = await senden({ name, referenz, nachricht, verbindlich: true });
    if (!ok) setBusy(false);
  };

  return (
    <form className="bl-antwort-form" onSubmit={(e) => void submit(e)} noValidate>
      <label className="field">
        <span>Ihr Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} required autoFocus />
      </label>
      <label className="field">
        <span>Ihre Bestellnummer (freiwillig)</span>
        <input value={referenz} onChange={(e) => setReferenz(e.target.value)} maxLength={50} autoComplete="off" />
      </label>
      <label className="field">
        <span>Nachricht an {b.firma.name} (freiwillig)</span>
        <textarea value={nachricht} onChange={(e) => setNachricht(e.target.value)} rows={3} maxLength={1000} />
      </label>
      <label className="check">
        <input type="checkbox" checked={verbindlich} onChange={(e) => setVerbindlich(e.target.checked)} required />
        <span>
          Ich nehme das Angebot {b.nummer} über {eurC(b.betrag ?? 0, b.waehrung)} verbindlich an.
        </span>
      </label>
      <p className="bl-klein">Gespeichert werden Ihr Name, Ihre Angaben und die Uhrzeit. {b.firma.name} wird benachrichtigt.</p>
      <div className="bl-antwort-knoepfe">
        <button type="submit" className="bl-btn primary" disabled={!bereit}>
          {busy ? 'Wird gesendet …' : 'Verbindlich annehmen'}
        </button>
        <button type="button" className="bl-btn" onClick={abbrechen} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// Ablehnen: Grund und Name freiwillig
function AblehnungForm({ firma, senden, abbrechen }: { firma: string; senden: (body: Record<string, unknown>) => Promise<boolean>; abbrechen: () => void }) {
  const [grund, setGrund] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const ok = await senden({ grund, name });
    if (!ok) setBusy(false);
  };

  return (
    <form className="bl-antwort-form" onSubmit={(e) => void submit(e)} noValidate>
      <label className="field">
        <span>Grund (freiwillig)</span>
        <textarea value={grund} onChange={(e) => setGrund(e.target.value)} rows={3} maxLength={1000} autoFocus />
      </label>
      <label className="field">
        <span>Ihr Name (freiwillig)</span>
        <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={100} />
      </label>
      <p className="bl-klein">{firma} wird benachrichtigt.</p>
      <div className="bl-antwort-knoepfe">
        <button type="submit" className="bl-btn" disabled={busy}>
          {busy ? 'Wird gesendet …' : 'Angebot ablehnen'}
        </button>
        <button type="button" className="bl-btn" onClick={abbrechen} disabled={busy}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Zahlung({ z, skonto, mahnung, w }: { z: NonNullable<Beleg['zahlung']>; skonto: Beleg['skonto']; mahnung: boolean; w?: string }) {
  return (
    <section className="bl-card bl-pay" aria-labelledby="bl-pay-h">
      <h2 id="bl-pay-h">Überweisung</h2>
      {mahnung && <p className="bl-note">Haben Sie schon überwiesen? Dann ist alles in Ordnung.</p>}
      {z.hinweis && <p className="bl-note">{z.hinweis}</p>}
      <dl className="bl-kv">
        <Zeile label="Empfänger" wert={z.empfaenger} />
        <Zeile label="IBAN" wert={z.iban} kopie={z.iban.replace(/\s/g, '')} mono />
        {z.bic && <Zeile label="BIC" wert={z.bic} mono />}
        {z.bank && <Zeile label="Bank" wert={z.bank} />}
        <Zeile label="Betrag" wert={eurC(z.betrag, w)} kopie={betragText(z.betrag)} />
        <Zeile label="Verwendungszweck" wert={z.verwendungszweck} kopie={z.verwendungszweck} />
      </dl>
      {skonto && (
        <p className="bl-skonto">
          Bei Zahlung bis {fmtDate(skonto.bis)} dürfen Sie {skonto.prozent.toLocaleString('de-DE')} % Skonto abziehen. Sie zahlen dann{' '}
          <b>{eurC(skonto.betrag, w)}</b>.
        </p>
      )}
      {z.girocode && (
        <figure className="bl-qr">
          <img src={z.girocode} alt="GiroCode für die Überweisung" width={152} height={152} />
          <figcaption>
            <b>Mit der Banking-App scannen</b>
            <span>Empfänger, IBAN, Betrag und Verwendungszweck sind dann schon ausgefüllt.</span>
          </figcaption>
        </figure>
      )}
    </section>
  );
}

function Zeile({ label, wert, kopie, mono }: { label: string; wert: string; kopie?: string; mono?: boolean }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        <span className={mono ? 'bl-mono' : undefined}>{wert}</span>
        {kopie !== undefined && <Kopieren text={kopie} label={label} />}
      </dd>
    </div>
  );
}

function Kopieren({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState<'ok' | 'fehler' | null>(null);
  useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => setDone(null), 2000);
    return () => clearTimeout(id);
  }, [done]);
  return (
    <button
      type="button"
      className={`bl-copy${done ? ` ${done}` : ''}`}
      aria-label={`${label} kopieren`}
      title={`${label} kopieren`}
      onClick={() => void kopieren(text).then((ok) => setDone(ok ? 'ok' : 'fehler'))}
    >
      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
        {done === 'ok' ? (
          <path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <>
            <rect x="7" y="7" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.7" />
            <path d="M13 4.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v7.5A1.5 1.5 0 0 0 4 13h.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
          </>
        )}
      </svg>
      <span className="bl-copy-t" aria-live="polite">
        {done === 'ok' ? 'Kopiert' : done === 'fehler' ? 'Nicht möglich' : 'Kopieren'}
      </span>
    </button>
  );
}

async function kopieren(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // ältere Browser oder ohne Erlaubnis: über ein unsichtbares Feld
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      ta.remove();
    }
  }
}

function Kontakt({ b }: { b: Beleg }) {
  const f = b.firma;
  const frage =
    b.art === 'angebot' && b.status === 'gueltig'
      ? 'Fragen zum Angebot? Melden Sie sich gern.'
      : b.art === 'angebot' && b.status === 'abgelaufen'
        ? 'Das Angebot gilt nicht mehr. Möchten Sie ein neues? Melden Sie sich gern.'
        : b.status === 'bezahlt'
        ? 'Vielen Dank für Ihre Zahlung. Bei Fragen melden Sie sich gern.'
        : b.art === 'auftrag'
          ? 'Fragen zu Ihrem Auftrag? Melden Sie sich gern.'
          : b.art === 'lieferschein'
            ? 'Fragen zur Lieferung? Melden Sie sich gern.'
            : 'Fragen zu diesem Beleg? Melden Sie sich gern.';
  return (
    <section className="bl-card bl-kontakt" aria-labelledby="bl-k-h">
      <h2 id="bl-k-h">Kontakt</h2>
      <p className="bl-note">{frage}</p>
      <p className="bl-adr">
        <b>{f.name}</b>
        <span>{f.anschrift}</span>
        {f.telefon && (
          <a href={`tel:${f.telefon.replace(/[^\d+]/g, '')}`}>{f.telefon}</a>
        )}
        {f.email && <a href={`mailto:${f.email}`}>{f.email}</a>}
      </p>
    </section>
  );
}

function Keine({ url }: { url: string }) {
  return (
    <p className="bl-keine">
      Die Vorschau geht in diesem Browser nicht. <a href={`${url}?download=1`}>PDF herunterladen</a>
    </p>
  );
}

// Unten: Pflichtangaben des Absenders, Impressum und Datenschutz der App, „Bereitgestellt mit …“
function Fuss({ b }: { b?: Beleg }) {
  const { kanzleiName } = useConfig();
  return (
    <footer className="bl-fuss">
      {b && (
        <p className="bl-pflicht">
          <span>
            {b.firma.name} · {b.firma.anschrift}
          </span>
          {b.firma.angaben.map((a) => (
            <span key={a}>{a}</span>
          ))}
        </p>
      )}
      {/* Billtoro: kleiner Hinweis mit Link zur Webseite, nur wenn der Server ihn schickt (Tarif ohne „ohneWerbung“) */}
      {isBilltoro() ? (
        b?.billtoroHinweis && (
          <p className="bl-app">
            {b.billtoroHinweis.url ? (
              <a href={b.billtoroHinweis.url} target="_blank" rel="noopener">
                {b.billtoroHinweis.text}
              </a>
            ) : (
              b.billtoroHinweis.text
            )}
          </p>
        )
      ) : (
        <p className="bl-app">Bereitgestellt mit {kanzleiName} Rechnungen</p>
      )}
      <LegalLinks />
    </footer>
  );
}
